import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useOutrightPreviewMutation,
  useOutrightSaveMutation,
  usePayDataBankLookupQuery,
  usePayDataTruckIdLookupQuery,
  type OutrightRowInput,
} from '@/store/api/procurementApi'
import { useLazySuggestXnttNoQuery } from '@/store/api/procurementApi'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, numSx, roSx } from '../purchase/dialogStyle'
import MassInputGrid, {
  type MassColumn,
  type MassRow,
  type MassVerdict,
} from './MassInputGrid'
import PreviewDataGrid, { type PreviewColumn } from './PreviewDataGrid'

// Bảng preview "Check data" — theo template /convert-table (freeze Contract No).
// Thứ tự + format khớp query Access ProcurementPay_WriteOutright.
const OUTRIGHT_PREVIEW_COLUMNS: PreviewColumn[] = [
  { key: 'date', label: 'Date', kind: 'date', width: 110 },
  { key: 'contract_no', label: 'Contract No', kind: 'text', width: 130, freeze: true },
  { key: 'payment_type', label: 'Payment Type', kind: 'text', width: 110 },
  { key: 'truck_id', label: 'Truck ID', kind: 'text', width: 90 },
  { key: 'price', label: 'Price', kind: 'number', width: 100, align: 'right' },
  { key: 'price_unit', label: 'Price Unit', kind: 'text', width: 90 },
  { key: 'requested_fx', label: 'Requested FX', kind: 'number', width: 110, align: 'right' },
  { key: 'bank_name', label: 'Bank Name', kind: 'text', width: 130 },
  { key: 'quantity_kg', label: 'Quantity (KG)', kind: 'number', width: 110, align: 'right', sum: true },
  { key: 'to_be_requested', label: 'To be Requested', kind: 'number', width: 120, align: 'right', sum: true },
  { key: 'advanced_vnd', label: 'Advanced (VND)', kind: 'number', width: 120, align: 'right', sum: true },
  { key: 'request_vnd', label: 'Request (VND)', kind: 'number', width: 120, align: 'right', sum: true },
  { key: 'vat_amount_vnd', label: 'VAT Amount (VND)', kind: 'number', width: 130, align: 'right', sum: true },
  { key: 'advanced_sub_vnd', label: 'Advanced sub (VND)', kind: 'number', width: 140, align: 'right', sum: true },
  { key: 'request_sub_vnd', label: 'Request sub (VND)', kind: 'number', width: 140, align: 'right', sum: true },
  { key: 'description', label: 'Description', kind: 'text', width: 160 },
]

// ---------------------------------------------------------------------------
// Props — exact signature for orchestrator wiring (SPEC C2).
// Guard (contract_type='Outright', P-*, not forced close) is done by the
// caller before opening — the dialog does not re-check it.
// ---------------------------------------------------------------------------
interface Props {
  open: boolean
  contractNo: string
  /** Raw text of the parent screen's "Requested FX" box; parsed here, BE falls back to fixed FX. */
  requestedFx: string
  onClose: () => void
  onSaved: () => void
}

// % Pay suggest — value list cố định (100%/80%/75%/90%/20%); freeSolo vẫn cho gõ số khác.
const PCT_PAY_SUGGEST = ['100%', '80%', '75%', '90%', '20%']

/** Cột input grid — thứ tự khớp Access table `public_tmp_addmany_pay_outright`
 *  ([FIELDS]: Payment Type, Truck ID, Bank Name, % Pay, Quantity KG, Advanced VND,
 *  VAT Amount VND, Description, Advanced sub VND). Combobox (table Lookup Access):
 *  Payment Type (value list 'Outright'), Truck ID (goods_allocation theo contract),
 *  Bank Name (cp_bank), % Pay (freeSolo suggest 100/80/75/90/20 %). */
const OUTRIGHT_COLUMNS: MassColumn[] = [
  { key: 'payment_type', label: 'Payment Type', kind: 'combo', width: 130, required: true, options: [{ value: 'Outright', label: 'Outright' }] },
  { key: 'truck_id', label: 'Truck ID', kind: 'combo', width: 120 },
  { key: 'bank_name', label: 'Bank Name', kind: 'combo', width: 150 },
  { key: 'pct_pay', label: '% Pay', kind: 'number', width: 90, suggest: PCT_PAY_SUGGEST },
  { key: 'quantity_kg', label: 'Quantity KG', kind: 'number', width: 110, sum: true },
  { key: 'advanced_vnd', label: 'Advanced VND', kind: 'number', width: 120, sum: true },
  { key: 'vat_amount_vnd', label: 'VAT Amount VND', kind: 'number', width: 130, sum: true },
  { key: 'description', label: 'Description', kind: 'text', width: 180 },
  { key: 'advanced_sub_vnd', label: 'Advanced sub VND', kind: 'number', width: 140, sum: true },
]

let ROW_SEQ = 0
const makeEmptyRow = (): MassRow => {
  const data: Record<string, string> = {}
  OUTRIGHT_COLUMNS.forEach((c) => {
    data[c.key] = ''
  })
  data.payment_type = 'Outright'
  return { id: ++ROW_SEQ, data }
}

/** Dòng có dữ liệu? — bỏ qua payment_type (fixed default), mirror Access isRowEmpty. */
const isRowFilled = (d: Record<string, string>): boolean =>
  OUTRIGHT_COLUMNS.some((c) => c.key !== 'payment_type' && (d[c.key] ?? '').trim() !== '')

/** Parse "1,234.5" → number | null. */
const parseNum = (s: string): number | null => {
  const t = (s ?? '').replace(/,/g, '').trim()
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

/** Parse % Pay — bỏ ký tự "%" (suggest "80%") rồi ra số (80). */
const parsePct = (s: string): number | null => parseNum((s ?? '').replace(/%/g, ''))

/** Format numeric — thousands sep, up to 2 decimals (Access 'Standard'). */
const fmtNum = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return ''
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

const toInputRow = (d: Record<string, string>): OutrightRowInput => ({
  payment_type: d.payment_type || null,
  truck_id: (d.truck_id ?? '').trim() || null,
  bank_name: (d.bank_name ?? '').trim() || null,
  quantity_kg: parseNum(d.quantity_kg),
  advanced_vnd: parseNum(d.advanced_vnd),
  advanced_sub_vnd: parseNum(d.advanced_sub_vnd),
  pct_pay: parsePct(d.pct_pay),
  description: (d.description ?? '').trim() || null,
  vat_amount_vnd: parseNum(d.vat_amount_vnd),
})

// Cross-check per-row — chỉ rule client-checkable (mirror BE _cdo_validate_rows):
// payment_type PHẢI = 'Outright'. Truck ID thuộc contract / Bank trong System Data
// cần lookup DB → BE bắt lúc Save (báo snack).
function validateRow(d: Record<string, string>): MassVerdict {
  const badFields = new Set<string>()
  const messages: string[] = []
  if (!isRowFilled(d)) return { ok: true, badFields, messages }
  if ((d.payment_type ?? '').trim() !== 'Outright') {
    badFields.add('payment_type')
    messages.push("Please type 'Outright' !")
  }
  return { ok: badFields.size === 0, badFields, messages }
}

/**
 * Contract Detail → "Add Pay Outright" batch dialog — converts Access
 * `frmProcurementQryDetailsAddPayOutright` ("Procurement - Pay (Outright)").
 *
 * Top: read-only contract context + editable XNTT No (pre-filled via the
 * shared suggest endpoint). Middle: Excel-like input grid (style Goods
 * Allocation › Mass Upload — dán từ Excel / checkbox / Copy·Delete·Reset /
 * cột status ✓✗). Bottom: "Refresh to Check" preview grid mirroring the Access
 * `ProcurementPay_WriteOutright` query (BE-computed); Save inserts every line.
 */
export default function ContractDetailAddPayOutrightDialog({
  open,
  contractNo,
  requestedFx,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()

  const [runPreview, { data: preview, isLoading: isPreviewing }] = useOutrightPreviewMutation()
  const [runSave, { isLoading: isSaving }] = useOutrightSaveMutation()
  const [suggestXntt] = useLazySuggestXnttNoQuery()

  // Combobox lookups (Access table Lookup): Truck ID theo contract, Bank list.
  const { data: truckData } = usePayDataTruckIdLookupQuery(
    { contractNo },
    { skip: !open || !contractNo },
  )
  const { data: bankData } = usePayDataBankLookupQuery(undefined, { skip: !open })

  const columns = useMemo<MassColumn[]>(
    () =>
      OUTRIGHT_COLUMNS.map((c) => {
        if (c.key === 'truck_id') return { ...c, options: truckData?.items ?? [] }
        if (c.key === 'bank_name') return { ...c, options: bankData?.items ?? [] }
        return c
      }),
    [truckData, bankData],
  )

  const [rows, setRows] = useState<MassRow[]>([makeEmptyRow()])
  const [selected, setSelected] = useState<Set<number>>(() => new Set())
  const [xnttNo, setXnttNo] = useState('')
  // Gate spinner riêng cho cụm context read-only (preview lần đầu) — body/grid nhập render ngay.
  const [contextLoading, setContextLoading] = useState(false)

  const parsedFx = useMemo(() => parseNum(requestedFx), [requestedFx])

  const verdicts = useMemo(() => rows.map((r) => validateRow(r.data)), [rows])
  const filledCount = useMemo(() => rows.filter((r) => isRowFilled(r.data)).length, [rows])
  const allValid = filledCount > 0 && verdicts.every((v) => v.ok)

  const payloadRows = (): OutrightRowInput[] =>
    rows.filter((r) => isRowFilled(r.data)).map((r) => toInputRow(r.data))

  // Signature payload input → trigger auto-refresh preview khi Input data đổi.
  const rowsSignature = useMemo(
    () => JSON.stringify(rows.filter((r) => isRowFilled(r.data)).map((r) => toInputRow(r.data))),
    [rows],
  )
  const skipAutoPreviewRef = useRef(false)
  const previewReqRef = useRef<{ abort?: () => void } | null>(null)

  // Re-init each time the dialog opens: reset grid, fetch header context
  // (preview with rows=[]) + suggest XNTT No.
  useEffect(() => {
    if (!open || !contractNo) return
    setRows([makeEmptyRow()])
    setSelected(new Set())
    setXnttNo('')
    setContextLoading(true)
    skipAutoPreviewRef.current = true // open đã tự preview rows=[] → bỏ lần auto đầu.
    void (async () => {
      try {
        await runPreview({ contract_no: contractNo, requested_fx: parsedFx, rows: [] }).unwrap()
      } catch {
        // preview is best-effort — context stays blank on error
      } finally {
        setContextLoading(false)
      }
    })()
    void (async () => {
      try {
        const sug = await suggestXntt({ contractNo }).unwrap()
        setXnttNo(sug.suggestedNo)
      } catch {
        // suggest is best-effort — user can still type the XNTT No manually
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contractNo])

  // Bắn preview ngay (abort request đang chạy → tối đa 1 in-flight). Dùng cho cả
  // auto-refresh (debounce) lẫn nút Refresh thủ công.
  const firePreview = () => {
    previewReqRef.current?.abort?.()
    const req = runPreview({ contract_no: contractNo, requested_fx: parsedFx, rows: payloadRows() })
    previewReqRef.current = req
    void req.unwrap().catch(() => {
      /* preview best-effort (kể cả abort) — lỗi thật sẽ hiện lại khi Save */
    })
  }

  // Auto-refresh preview (debounce 2s) khi Input data / Requested FX đổi. Nút
  // Refresh (trên hàng Filter của preview) để cập nhật ngay không đợi debounce.
  useEffect(() => {
    if (!open || !contractNo) return
    if (skipAutoPreviewRef.current) {
      skipAutoPreviewRef.current = false
      return
    }
    const handle = setTimeout(firePreview, 2000)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contractNo, parsedFx, rowsSignature])

  const handleSave = async () => {
    if (filledCount === 0) {
      snack.warning('Please input at least one payment row !')
      return
    }
    const firstBad = verdicts.findIndex((v) => !v.ok)
    if (firstBad >= 0) {
      snack.warning(`Row ${firstBad + 1}: ${verdicts[firstBad].messages[0]}`)
      return
    }
    try {
      const res = await runSave({
        contract_no: contractNo,
        requested_fx: parsedFx,
        rows: payloadRows(),
        xntt_no: xnttNo.trim() || null,
      }).unwrap()
      snack.success(`Raw records: ${res.raw_records} — Error: ${res.errors}`)
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to save'))
    }
  }

  const roField = (label: string, value: string, numeric = false) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      value={value}
      InputProps={{ readOnly: true }}
      InputLabelProps={{ shrink: true }}
      sx={numeric ? [numSx, roSx] : roSx}
    />
  )

  const previewRows = preview?.rows ?? []

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper">
      <DialogTitle
        sx={{ pb: 1, pt: 1.25, px: 2, display: 'flex', alignItems: 'center', fontWeight: 700, fontSize: '1rem' }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ width: '100%' }}>
          <Box component="span" title="Inbound Logistics - Pay (Outright)">
            Inbound Logistics - Pay (Outright)
          </Box>
          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
            <Button
              variant="contained"
              size="small"
              color="success"
              onClick={() => void handleSave()}
              disabled={isSaving || isPreviewing || !allValid}
              sx={dialogHeaderBtnSx}
            >
              {isSaving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
            </Button>
            <Button variant="outlined" size="small" onClick={onClose} disabled={isSaving} sx={dialogHeaderBtnSx}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {/* ----- Contract context (label left / value right, 2 columns) ----- */}
        {contextLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2, mb: 1.5 }}>
            <CircularProgress size={18} />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
              mb: 1.5,
            }}
          >
            <Box>{roField('Contract No', contractNo)}</Box>
            <Box>{roField('Date', dayjs().format('DD-MMM-YYYY'))}</Box>
            <Box>{roField('To be Receive', fmtNum(preview?.pending), true)}</Box>
            <Box>{roField('Fixed FX (on Contract)', fmtNum(preview?.fixed_fx), true)}</Box>

            <Box>{roField('Requested FX', fmtNum(preview?.requested_fx), true)}</Box>
            <Box>
              <TextField
                label="XNTT No"
                size="small"
                fullWidth
                value={xnttNo}
                onChange={(e) => setXnttNo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={isSaving}
              />
            </Box>

            <Box>{roField('Applicable VAT', preview?.vat_txt ?? '')}</Box>
            <Box>{roField('Pending.Outright', fmtNum(preview?.pending_outright), true)}</Box>
          </Box>
        )}

        {/* ----- Input grid (Access subForm: tmp_addmany_pay_outright) ----- */}
        <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>Input data</Typography>
          <Typography sx={{ color: 'error.main', fontStyle: 'italic', fontSize: '0.7rem' }}>
            * Advanced auto-filled (Contract + TruckID + Quantity) when &quot;Advanced VND&quot; is blank
          </Typography>
        </Stack>
        <MassInputGrid
          open={open}
          columns={columns}
          rows={rows}
          setRows={setRows}
          selected={selected}
          setSelected={setSelected}
          makeEmptyRow={makeEmptyRow}
          isRowFilled={isRowFilled}
          verdicts={verdicts}
          filledCount={filledCount}
          disabled={isSaving}
          emptyStateText="Enter at least one payment row to check"
          snackbar={snack}
          rowNoun="row"
          showCopy={false}
        />

        {/* ----- Check section: title + New Pending gộp vào hàng Filter của preview ----- */}
        <Divider sx={{ my: 1.5 }} />
        <PreviewDataGrid
          columns={OUTRIGHT_PREVIEW_COLUMNS}
          rows={previewRows as unknown as Record<string, unknown>[]}
          emptyText={isPreviewing ? 'Loading…' : 'No preview rows — input data above.'}
          height={300}
          title="Check data (auto-updated)"
          loading={isPreviewing}
          onRefresh={firePreview}
          rightSlot={
            <Box sx={{ width: { xs: '100%', sm: 240 } }}>
              {roField('New Pending.Outright', fmtNum(preview?.new_pending), true)}
            </Box>
          }
        />
      </DialogContent>
    </Dialog>
  )
}
